// apps/client/src/services/projects.ts
import { AccountClass, AccountType, ResearchActivity } from '@avanzu/types';
import { ProjectAccountsResponse, ProjectAccountTransactionsResponse } from '@avanzu/types';
import { apiClient } from './apiClient';

interface FetchProjectActivitiesResponse {
  success: boolean;
  activities: ResearchActivity[];
  message?: string;
}

/**
 * Fetch project activities
 */
export const fetchProjectActivities = async (
  organisationId: string,
  projectId: string
): Promise<FetchProjectActivitiesResponse> => {
  try {
    const response = await apiClient.get(
      `/organisations/${organisationId}/projects/${projectId}/activities`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        activities: [],
        message: errorData.message || 'Failed to fetch activities',
      };
    }

    const data: FetchProjectActivitiesResponse = await response.json();
    return { success: true, activities: data.activities };
  } catch (error) {
    return {
      success: false,
      activities: [],
      message: error instanceof Error ? error.message : 'Failed to fetch activities',
    };
  }
};

/**
 * Fetch accounts that have allocations for a specific project
 */
export const fetchProjectAccounts = async (
  organisationId: string,
  projectId: string
): Promise<ProjectAccountsResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/projects/${projectId}/accounts`
  );

  if (!response.ok) {
    const errorData = await response.json();
    const errorMessage = errorData?.message || 'Failed to fetch project accounts';
    throw new Error(errorMessage);
  }

  const data: ProjectAccountsResponse = await response.json();
  return data;
};

/**
 * Fetch transactions for a specific account in a project
 */
export const fetchProjectAccountTransactions = async (
  organisationId: string,
  projectId: string,
  accountId: string
): Promise<ProjectAccountTransactionsResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/projects/${projectId}/accounts/${accountId}/transactions`
  );

  if (!response.ok) {
    const errorData = await response.json();
    const errorMessage = errorData?.message || 'Failed to fetch account transactions';
    throw new Error(errorMessage);
  }

  const data: ProjectAccountTransactionsResponse = await response.json();
  return data;
};

/**
 * Fetch project account rules - accounts with their allocation rules for a specific project
 */
export const fetchProjectAccountRules = async (
  organisationId: string,
  projectId: string,
  options?: {
    types?: AccountType[];
    classes?: AccountClass[];
    allocatedOnly?: boolean;
  }
): Promise<ProjectAccountsResponse> => {
  try {
    // Build query parameters
    const params = new URLSearchParams();

    if (options?.types && options.types.length > 0) {
      params.append('types', options.types.join(','));
    }

    if (options?.classes && options.classes.length > 0) {
      params.append('classes', options.classes.join(','));
    }

    if (options?.allocatedOnly) {
      params.append('allocatedOnly', 'true');
    }

    const queryString = params.toString();
    const url = `/organisations/${organisationId}/projects/${projectId}/accounts/rules${
      queryString ? `?${queryString}` : ''
    }`;

    const response = await apiClient.get(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch project account rules');
    }

    const data: ProjectAccountsResponse = await response.json();
    return data;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to fetch project account rules'
    );
  }
};

/**
 * Download project Excel workbook export
 */
export const downloadProjectWorkbook = async (
  organisationId: string,
  projectId: string
): Promise<Blob> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/projects/${projectId}/export/workbook`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to download workbook');
  }

  return await response.blob();
};

/**
 * Download project Word plan document
 */
export const downloadProjectPlan = async (
  organisationId: string,
  projectId: string
): Promise<Blob> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/projects/${projectId}/export/plan`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to download plan document');
  }

  return await response.blob();
};
